import { MATRIX_ACTION_TYPES } from './types';
import matrixService from '../services/matrixService';
import { isUserAdmin } from '../utils/function';
import { reportError } from '../sentry';
import { MATRIX_ROOMS_PAGE_LIMIT, MATRIX_ROOMS_MAX_AUTO_PAGES } from '../constants/message';
import {
  normalizeMatrixRoomsPayload,
  computeMatrixRoomsHasMore,
} from '../../views/Pages/Message/hooks/matrixRoomsPagination';

/** If Redux still shows an in-flight rooms fetch for this long, unlock and retry (tab sleep / hung post-processing). */
const STALE_IN_FLIGHT_MATRIX_ROOMS_MS = 45_000;
/** GET uses axios timeout; this caps the entire fetchRooms promise (includes signed-profile URL fan-out). */
const HARD_MATRIX_ROOMS_FETCH_PROMISE_MS = 60_000;
const STALE_IN_FLIGHT_MATRIX_ALL_ROOMS_MS = STALE_IN_FLIGHT_MATRIX_ROOMS_MS;
const HARD_MATRIX_ALL_ROOMS_FETCH_PROMISE_MS = HARD_MATRIX_ROOMS_FETCH_PROMISE_MS;

function raceWithTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const to = setTimeout(() => reject(new Error(label)), ms);
    promise.then(
      (v) => {
        clearTimeout(to);
        resolve(v);
      },
      (e) => {
        clearTimeout(to);
        reject(e);
      },
    );
  });
}

const adminMatrixSkipResponse = (message = 'Matrix is disabled for admin users') => ({
  success: true,
  skipped: true,
  message,
});

async function fetchMatrixRoomsPagesUntilComplete({
  dispatch,
  getState,
  force,
  fetchPage,
  paginationSelector,
  hasLoadedSelector,
  maxPages = MATRIX_ROOMS_MAX_AUTO_PAGES,
}) {
  const matrix = getState().matrix;
  const pagination = paginationSelector(matrix);

  if (!force && hasLoadedSelector(matrix) && pagination && !pagination.hasMore) {
    return { success: true, cached: true, complete: true };
  }

  if (!force && hasLoadedSelector(matrix) && pagination?.hasMore) {
    let pagesFetched = 0;
    while (pagesFetched < maxPages) {
      const current = getState().matrix;
      const currentPagination = paginationSelector(current);
      if (!currentPagination?.hasMore) {
        return { success: true, complete: true };
      }

      const next = await fetchPage({ append: true });
      if (!next?.success) {
        return next;
      }
      pagesFetched += 1;
    }
    return { success: true, complete: true };
  }

  const first = await fetchPage({ force, page: 1, append: false });
  if (!first?.success && !first?.cached) {
    return first;
  }

  let pagesFetched = 1;
  while (pagesFetched < maxPages) {
    const current = getState().matrix;
    const currentPagination = paginationSelector(current);
    if (!currentPagination?.hasMore) {
      return { success: true, complete: true };
    }

    const next = await fetchPage({ append: true });
    if (!next?.success) {
      return next;
    }
    pagesFetched += 1;
  }

  return { success: true, complete: true };
}

async function dispatchMatrixRoomsFetch({
  dispatch,
  getState,
  force,
  page: requestedPage,
  limit = MATRIX_ROOMS_PAGE_LIMIT,
  append = false,
  fetchFn,
  stateKeys,
  actionTypes,
  staleMs,
  hardTimeoutMs,
  adminSkipMessage,
  failureLabel,
}) {
  if (isUserAdmin()) {
    const payload = {
      rooms: { rooms: [], page: 1, limit, total: 0, count: 0 },
      pagination: { page: 1, total: 0, hasMore: false },
      message: adminSkipMessage,
      fetchedAt: Date.now(),
    };
    dispatch({
      type: actionTypes.SUCCESS,
      payload,
    });
    return { ...adminMatrixSkipResponse(payload.message), data: payload.rooms };
  }

  let { matrix } = getState();
  const paginationKey = stateKeys.pagination;
  const loadingMoreKey = stateKeys.loadingMore;
  const currentPagination = matrix?.[paginationKey] || { page: 0, total: 0, hasMore: false };

  if (append) {
    if (matrix?.[loadingMoreKey] || matrix?.[stateKeys.loading]) {
      return { success: false, error: 'Request already in progress' };
    }
    if (!currentPagination.hasMore) {
      return { success: false, error: 'No more rooms' };
    }
  }

  if (matrix?.[stateKeys.loading] && matrix?.[stateKeys.fetchRequestedAt] > 0) {
    const staleDurationMs = Date.now() - matrix[stateKeys.fetchRequestedAt];
    if (staleDurationMs >= staleMs) {
      dispatch({ type: actionTypes.STALE_ABORT });
      matrix = getState().matrix;
    }
  }
  if (!append && matrix?.[stateKeys.loading]) {
    return { success: false, error: 'Request already in progress' };
  }

  if (!force && !append && matrix?.[stateKeys.hasLoaded]) {
    return { success: true, data: matrix?.[stateKeys.rooms], cached: true };
  }

  const page = append
    ? (requestedPage ?? (currentPagination.page || 0) + 1)
    : (requestedPage ?? 1);
  const hadLoadedBefore = Boolean(matrix?.[stateKeys.hasLoaded]);
  const refreshMerge = Boolean(force && hadLoadedBefore && !append);

  dispatch({
    type: actionTypes.REQUEST,
    payload: { append, page },
  });

  try {
    const response = await raceWithTimeout(
      fetchFn(page, limit),
      hardTimeoutMs,
      `${failureLabel}_TIMED_OUT`,
    );

    if (response.success) {
      const normalizedRoomsPayload = normalizeMatrixRoomsPayload(response);
      const hasMore = computeMatrixRoomsHasMore(normalizedRoomsPayload);

      dispatch({
        type: actionTypes.SUCCESS,
        payload: {
          rooms: normalizedRoomsPayload,
          pagination: {
            page: normalizedRoomsPayload.page,
            total: normalizedRoomsPayload.total,
            hasMore,
          },
          message: response.message,
          fetchedAt: Date.now(),
          append,
          refreshMerge,
        },
      });

      return { success: true, data: normalizedRoomsPayload };
    }

    dispatch({
      type: actionTypes.FAILURE,
      payload: {
        error: response.message || `Failed to fetch ${failureLabel}`,
        status: response.status,
        append,
      },
    });

    return { success: false, error: response.message };
  } catch (error) {
    reportError(error, failureLabel);
    dispatch({
      type: actionTypes.FAILURE,
      payload: {
        error: error.message || `An unexpected error occurred while fetching ${failureLabel}`,
        status: error.status || 500,
        append,
      },
    });

    return { success: false, error: error.message };
  }
}

/**
 * Register user with Matrix
 * Access token is automatically added by axios interceptor
 * @returns {Promise<Object>} Promise resolving to registration result with success, data, and message/error
 */
export const registerMatrix = async () => {
  if (isUserAdmin()) {
    return adminMatrixSkipResponse('Matrix registration skipped for admin users');
  }

  try {
    const response = await matrixService.register();
    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Matrix registration failed',
      data: error.response?.data || null
    };
  }
};

/**
 * Refresh Matrix token by getting short-lived token
 * Access token is automatically added by axios interceptor
 * @returns {Promise<Object>} Promise resolving to token refresh result with success, data, and message/error
 */
export const refreshMatrixToken = async () => {
  if (isUserAdmin()) {
    return adminMatrixSkipResponse('Matrix token refresh skipped for admin users');
  }

  try {
    const response = await matrixService.getShortLivedToken();
    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Matrix token refresh failed',
      data: error.response?.data || null
    };
  }
};

/**
 * Fetch Matrix rooms action
 * Prevents duplicate concurrent requests by checking loading state
 * Also prevents repeated fetches on route remounts by short-circuiting once data is loaded.
 * @returns {Function} Redux thunk function
 */
export const fetchMatrixRooms =
  ({ force = false, page, limit = MATRIX_ROOMS_PAGE_LIMIT, append = false } = {}) =>
    async (dispatch, getState) =>
      dispatchMatrixRoomsFetch({
        dispatch,
        getState,
        force,
        page,
        limit,
        append,
        fetchFn: (p, l) => matrixService.fetchRooms({ page: p, limit: l }),
        stateKeys: {
          loading: 'loading',
          loadingMore: 'roomsLoadingMore',
          hasLoaded: 'hasRoomsLoaded',
          rooms: 'rooms',
          fetchRequestedAt: 'roomsFetchRequestedAt',
          pagination: 'roomsPagination',
        },
        actionTypes: {
          REQUEST: MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOMS_REQUEST,
          SUCCESS: MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOMS_SUCCESS,
          FAILURE: MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOMS_FAILURE,
          STALE_ABORT: MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOMS_STALE_ABORT,
        },
        staleMs: STALE_IN_FLIGHT_MATRIX_ROOMS_MS,
        hardTimeoutMs: HARD_MATRIX_ROOMS_FETCH_PROMISE_MS,
        adminSkipMessage: 'Matrix rooms skipped for admin users',
        failureLabel: 'Matrix Rooms API',
      });

/**
 * Fetch all Matrix rooms action (New Message page)
 * Same response shape as fetchMatrixRooms but uses /matrix/all-rooms.
 * @returns {Function} Redux thunk function
 */
export const fetchMatrixAllRooms =
  ({ force = false, page, limit = MATRIX_ROOMS_PAGE_LIMIT, append = false } = {}) =>
    async (dispatch, getState) =>
      dispatchMatrixRoomsFetch({
        dispatch,
        getState,
        force,
        page,
        limit,
        append,
        fetchFn: (p, l) => matrixService.fetchAllRooms({ page: p, limit: l }),
        stateKeys: {
          loading: 'allRoomsLoading',
          loadingMore: 'allRoomsLoadingMore',
          hasLoaded: 'hasAllRoomsLoaded',
          rooms: 'allRooms',
          fetchRequestedAt: 'allRoomsFetchRequestedAt',
          pagination: 'allRoomsPagination',
        },
        actionTypes: {
          REQUEST: MATRIX_ACTION_TYPES.FETCH_MATRIX_ALL_ROOMS_REQUEST,
          SUCCESS: MATRIX_ACTION_TYPES.FETCH_MATRIX_ALL_ROOMS_SUCCESS,
          FAILURE: MATRIX_ACTION_TYPES.FETCH_MATRIX_ALL_ROOMS_FAILURE,
          STALE_ABORT: MATRIX_ACTION_TYPES.FETCH_MATRIX_ALL_ROOMS_STALE_ABORT,
        },
        staleMs: STALE_IN_FLIGHT_MATRIX_ALL_ROOMS_MS,
        hardTimeoutMs: HARD_MATRIX_ALL_ROOMS_FETCH_PROMISE_MS,
        adminSkipMessage: 'Matrix all rooms skipped for admin users',
        failureLabel: 'Matrix All Rooms API',
      });

/**
 * Fetch all paginated Matrix rooms (page 1 → until hasMore is false).
 * Used on initial load and after rooms-update-hasmessage so metadata covers every room.
 */
export const fetchMatrixRoomsUntilComplete =
  ({ force = false } = {}) =>
    async (dispatch, getState) =>
      fetchMatrixRoomsPagesUntilComplete({
        dispatch,
        getState,
        force,
        fetchPage: (opts) => dispatch(fetchMatrixRooms(opts)),
        paginationSelector: (matrix) => matrix?.roomsPagination,
        hasLoadedSelector: (matrix) => matrix?.hasRoomsLoaded,
      });

/**
 * Fetch all paginated Matrix all-rooms (New Message page).
 */
export const fetchMatrixAllRoomsUntilComplete =
  ({ force = false } = {}) =>
    async (dispatch, getState) =>
      fetchMatrixRoomsPagesUntilComplete({
        dispatch,
        getState,
        force,
        fetchPage: (opts) => dispatch(fetchMatrixAllRooms(opts)),
        paginationSelector: (matrix) => matrix?.allRoomsPagination,
        hasLoadedSelector: (matrix) => matrix?.hasAllRoomsLoaded,
      });

/**
 * Update Matrix rooms has-message flag for rooms missing backend user metadata.
 * @param {string[]} roomIds - Matrix room ids
 * @returns {Promise<Object>}
 */
export const updateMatrixRoomsHasMessage = async (roomIds) => {
  if (isUserAdmin()) {
    return adminMatrixSkipResponse('Update Matrix rooms has-message skipped for admin users');
  }

  try {
    if (!Array.isArray(roomIds) || roomIds.length === 0) {
      return { success: false, error: 'Room IDs are required' };
    }

    const response = await matrixService.updateRoomsHasMessage(roomIds);
    if (response.success) {
      return { success: true, data: response.data };
    }
    return { success: false, error: response.message };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to update Matrix rooms has-message',
      data: error.data || error.response?.data || null,
    };
  }
};

/**
 * Clear Matrix errors action
 * @returns {Object} Action object
 */
export const clearMatrixErrors = () => ({
  type: MATRIX_ACTION_TYPES.CLEAR_MATRIX_ERRORS
});

/**
 * Fetch Matrix room details by room id
 * Access token is automatically added by axios interceptor
 * @param {string} roomId - Matrix room id
 * @returns {Promise<Object>} Promise resolving to room result with success, data, and message/error
 */
export const createOrGetMatrixRoom = async (userId) => {
  if (isUserAdmin()) {
    return adminMatrixSkipResponse('Create/Get Matrix room skipped for admin users');
  }

  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }
    const response = await matrixService.createOrGetMatrixRoom(userId);
    if (response.success) {
      return { success: true, data: response.data };
    }
    return { success: false, error: response.message };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to create or get Matrix room',
      data: error.data || error.response?.data || null
    };
  }
};

/**
 * Delete Matrix room by room id
 * Access token is automatically added by axios interceptor
 * @param {string} roomId - Matrix room id
 * @returns {Promise<Object>} Promise resolving to delete result with success, data, and message/error
 */
export const deleteMatrixRoom = async (roomId) => {
  if (isUserAdmin()) {
    return adminMatrixSkipResponse('Delete Matrix room skipped for admin users');
  }

  try {
    if (!roomId) {
      return { success: false, error: 'Room ID is required' };
    }
    const response = await matrixService.deleteMatrixRoom(roomId);
    if (response.success) {
      return { success: true, data: response.data };
    }
    return { success: false, error: response.message };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to delete Matrix room',
      data: error.data || error.response?.data || null
    };
  }
};

/**
 * Leave Matrix room by room id
 * Access token is automatically added by axios interceptor
 * @param {string} roomId - Matrix room id
 * @returns {Promise<Object>} Promise resolving to leave result with success, data, and message/error
 */
export const leaveMatrixRoom = async (roomId) => {
  if (isUserAdmin()) {
    return adminMatrixSkipResponse('Leave Matrix room skipped for admin users');
  }

  try {
    if (!roomId) {
      return { success: false, error: 'Room ID is required' };
    }
    const response = await matrixService.leaveMatrixRoom(roomId);
    if (response.success) {
      return { success: true, data: response.data };
    }
    return { success: false, error: response.message };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to leave Matrix room',
      data: error.data || error.response?.data || null
    };
  }
};

/**
 * Fetch Matrix room info by user id (route param)
 * Access token is automatically added by axios interceptor
 * @param {string} userId - User id
 * @returns {Function} Redux thunk function
 */
export const getMatrixRoomInfo =
  (userId, { force = false, background = false } = {}) =>
    async (dispatch, getState) => {
      if (isUserAdmin()) {
        dispatch({
          type: MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOM_INFO_SUCCESS,
          payload: {
            userId,
            data: null,
            fetchedAt: Date.now(),
          },
        });
        return { ...adminMatrixSkipResponse('Matrix room info skipped for admin users'), data: null };
      }

      try {
        if (!userId) {
          return { success: false, error: 'User ID is required' };
        }

        const { matrix } = getState();
        const existing = matrix?.roomInfoByUserId?.[userId];

        // Prevent duplicate concurrent requests per userId
        if (existing?.loading) {
          return { success: false, error: 'Request already in progress' };
        }

        // Cache: if we already have data for this userId in this SPA session, don't refetch.
        if (!force && existing?.data) {
          return { success: true, data: existing.data, cached: true };
        }

        if (!background) {
          dispatch({
            type: MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOM_INFO_REQUEST,
            payload: { userId },
          });
        }

        const response = await matrixService.getMatrixRoomInfo(userId);
        if (response.success) {
          dispatch({
            type: MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOM_INFO_SUCCESS,
            payload: {
              userId,
              data: response.data,
              fetchedAt: Date.now(),
            },
          });
          return { success: true, data: response.data };
        }

        dispatch({
          type: MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOM_INFO_FAILURE,
          payload: {
            userId,
            error: response.message || 'Failed to fetch Matrix room info',
            status: response.status,
          },
        });
        return { success: false, error: response.message };
      } catch (error) {
        reportError(error, 'Matrix Room Info API');
        dispatch({
          type: MATRIX_ACTION_TYPES.FETCH_MATRIX_ROOM_INFO_FAILURE,
          payload: {
            userId,
            error: error.message || 'An unexpected error occurred while fetching Matrix room info',
            status: error.status || 500,
          },
        });
        return {
          success: false,
          error: error.message || 'Failed to fetch Matrix room info',
          data: error.data || error.response?.data || null
        };
      }
    };

/**
 * Fetch restricted words for messages
 * Access token is automatically added by axios interceptor
 * @returns {Function} Redux thunk function
 */
export const fetchRestrictedWords =
  ({ force = false } = {}) =>
    async (dispatch, getState) => {
      if (isUserAdmin()) {
        dispatch({
          type: MATRIX_ACTION_TYPES.FETCH_RESTRICTED_WORDS_SUCCESS,
          payload: { data: [], fetchedAt: Date.now() },
        });
        return { ...adminMatrixSkipResponse('Restricted words fetch skipped for admin users'), data: [] };
      }

      const { matrix } = getState();
      if (matrix?.restrictedWords?.loading) {
        return { success: false, error: 'Request already in progress' };
      }
      if (!force && matrix?.restrictedWords?.data) {
        return { success: true, data: matrix.restrictedWords.data, cached: true };
      }

      dispatch({ type: MATRIX_ACTION_TYPES.FETCH_RESTRICTED_WORDS_REQUEST });

      try {
        const response = await matrixService.getRestrictedWords();
        if (response.success) {
          dispatch({
            type: MATRIX_ACTION_TYPES.FETCH_RESTRICTED_WORDS_SUCCESS,
            payload: { data: response.data, fetchedAt: Date.now() },
          });
          return { success: true, data: response.data };
        }
        dispatch({
          type: MATRIX_ACTION_TYPES.FETCH_RESTRICTED_WORDS_FAILURE,
          payload: { error: response.message || 'Failed to fetch restricted words', status: response.status },
        });
        return { success: false, error: response.message };
      } catch (error) {
        reportError(error, 'Restricted words API');
        dispatch({
          type: MATRIX_ACTION_TYPES.FETCH_RESTRICTED_WORDS_FAILURE,
          payload: {
            error: error.message || 'An unexpected error occurred while fetching restricted words',
            status: error.status || 500,
          },
        });
        return { success: false, error: error.message };
      }
    };

/**
 * Reply to message tips (creator replying to subscriber's tip message)
 * Access token is automatically added by axios interceptor
 * @param {string} subscriberUserId - Subscriber (other user) id
 * @param {string} creatorMessage - Creator reply message
 * @returns {Promise<Object>} Promise resolving to reply result with success, data, and message/error
 */
export const replyToMessageTips = async (subscriberUserId, creatorMessage) => {
  if (isUserAdmin()) {
    return adminMatrixSkipResponse('Reply to message tips skipped for admin users');
  }

  try {
    if (!subscriberUserId) {
      return { success: false, error: 'Subscriber user ID is required' };
    }
    const response = await matrixService.replyToMessageTips(subscriberUserId, creatorMessage);
    if (response.success) {
      return { success: true, data: response.data };
    }
    return { success: false, error: response.message };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to reply to message tips',
      data: error.data || error.response?.data || null
    };
  }
};

/**
 * Attach Matrix event id to a message tip
 * Access token is automatically added by axios interceptor
 * @param {{ matrixEventId: string, tipId: string }} payload
 * @returns {Promise<Object>} Promise resolving to result with success, data, and message/error
 */
export const addMatrixIdForMessageTip = async ({ matrixEventId, tipId }) => {
  if (isUserAdmin()) {
    return adminMatrixSkipResponse('Add Matrix id for message tip skipped for admin users');
  }

  try {
    if (!matrixEventId || !tipId) {
      return { success: false, error: 'matrixEventId and tipId are required' };
    }

    const response = await matrixService.addMatrixIdForMessageTip({ matrixEventId, tipId });

    if (response.success) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Matrix id added for message tip successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to add Matrix id for message tip',
      data: response.data || null,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to add Matrix id for message tip',
      data: error.data || error.response?.data || null,
    };
  }
};

/**
 * Notify backend that a chat message was sent (without dispatch).
 * @param {{
 *   recipientUserId: string,
 *   previewText: string,
 *   isPriority?: boolean,
 *   senderUserRole: number,
 *   receiverUserRole: number,
 * }} payload
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const notifyMessageRecipient = async (payload) => {
  try {
    const recipientUserId =
      typeof payload?.recipientUserId === 'string' ? payload.recipientUserId.trim() : '';
    const previewText = typeof payload?.previewText === 'string' ? payload.previewText.trim() : '';

    if (!recipientUserId) {
      return { success: false, error: 'Recipient user ID is required' };
    }
    if (!previewText) {
      return { success: false, error: 'Preview text is required' };
    }
    if (payload?.senderUserRole == null || payload?.receiverUserRole == null) {
      return { success: false, error: 'Sender and receiver roles are required' };
    }

    const response = await matrixService.notifyMessage({
      recipientUserId,
      previewText,
      isPriority: Boolean(payload.isPriority),
      senderUserRole: Number(payload.senderUserRole),
      receiverUserRole: Number(payload.receiverUserRole),
    });

    if (response.success) {
      return { success: true, data: response.data };
    }

    return { success: false, error: response.message || 'Failed to send message notify' };
  } catch (error) {
    return { success: false, error: error.message || 'Failed to send message notify' };
  }
};